// lib/list/listReferenceImages.ts
import type { LockableFile } from '#types/types';
import { isInternalAssetUrl } from '#lib/azure-shared';

export async function loadListReferenceImages(urls: readonly string[], maxReferences: number, options: {
  signal?: AbortSignal;
  mainImageUrl?: string | null;
  cache?: Map<string, Promise<LockableFile>>;
} = {}): Promise<LockableFile[]> {
  const unique = [...new Set(urls)].filter(url => url !== options.mainImageUrl);
  if (unique.length && (!Number.isFinite(maxReferences) || maxReferences < unique.length)) {
    throw new Error('The selected model cannot receive all mentioned images. Choose a reference-capable model or fewer references.');
  }
  return Promise.all(unique.map(async (url, index) => {
    if (!isInternalAssetUrl(url)) throw new Error('Choose reference images from this List project or attach them to this message.');
    const cached = options.cache?.get(url);
    if (cached) return cached;
    const pending = (async () => {
      const response = await fetch(url, { signal: options.signal });
      if (!response.ok) throw new Error('A selected output could not be loaded. No generation was started.');
      const blob = await response.blob();
      if (!['image/jpeg', 'image/png', 'image/webp'].includes(blob.type)) throw new Error('Only image outputs can be used as references.');
      return { id: url, file: new File([blob], `reference-${index}`, { type: blob.type }), locked: false };
    })();
    options.cache?.set(url, pending);
    return pending;
  }));
}
